import React, { startTransition, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Menu as MenuIcon, ExpandMore, SwapHoriz } from "@mui/icons-material";
import SupportButton from "@/components/common/SupportButton";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "oidc-react";
import { useUser } from "@/hooks/useUser";
import { useIsAdmin } from "@/hooks/usePermissions";
import { useAgronodKonto } from "@/contexts/AgronodKontoContext";
import { useQueryClient } from "@tanstack/react-query";
import { ROUTES } from "@/constants";
import { FLOATING_ELEMENTS } from "@/constants/layout";

// Import Figma icons
import BrandLogo from "@/components/common/BrandLogo";
import NavDokumentIcon from "@/components/icons/NavDokumentIcon";
import NavHemIcon from "@/components/icons/NavHemIcon";
import NavKontrollerIcon from "@/components/icons/NavKontrollerIcon";
// Hidden for now
// import NavNotiserIcon from "@/components/icons/NavNotiserIcon";
// import NavInstallningarIcon from "@/components/icons/NavInstallningarIcon";
import NavKontrollpanelIcon from "@/components/icons/NavKontrollpanelIcon";
import NavForetagAnvandareIcon from "@/components/icons/NavForetagAnvandareIcon";
import FarmIcon from "@/components/icons/FarmIcon";
import { getUserDisplayName } from "@/types/auth";
import { useFarmName } from "@/hooks/useFarmName";

interface MainLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 288; // 272px content + 8px padding on each side

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [kontoAnchorEl, setKontoAnchorEl] = useState<null | HTMLElement>(null);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [toolbarHeight, setToolbarHeight] = useState<number>(
    FLOATING_ELEMENTS.TOOLBAR.HEIGHT
  );

  const theme = useTheme();
  // Hide drawer below 1400px to ensure adequate content width
  // At 1400px with drawer (288px) and padding (128px), content width is ~984px
  const isMobile = useMediaQuery(theme.breakpoints.down(1400));
  const navigate = useNavigate();
  const location = useLocation();
  const { signOutRedirect } = useAuth();
  const { user } = useUser();
  const isAdmin = useIsAdmin();
  const { selectedKontoId, availableKonton, setSelectedKontoId } = useAgronodKonto();
  const queryClient = useQueryClient();

  // Listen for toolbar visibility and height changes
  React.useEffect(() => {
    (window as any).__setToolbarVisible = (
      visible: boolean,
      height?: number
    ) => {
      setToolbarVisible(visible);
      if (height !== undefined) {
        setToolbarHeight(height);
      }
    };
    return () => {
      delete (window as any).__setToolbarVisible;
    };
  }, []);

  // Close mobile drawer on route change to prevent overlay from getting stuck
  React.useEffect(() => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  }, [location.pathname]);

  // Reset mobileOpen whenever the viewport leaves the mobile breakpoint.
  // The temporary drawer's wrapper gets `display: none` at >= 1400px, but
  // MUI Modal renders its backdrop in a separate portal that doesn't
  // inherit the wrapper's `display: none`. Combined with `keepMounted: true`,
  // a viewport resize while the drawer is open leaves the backdrop pinned
  // in the DOM. This effect closes the drawer first so the backdrop is
  // properly removed before the wrapper hides.
  React.useEffect(() => {
    if (!isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, mobileOpen]);
  const userDisplayName = getUserDisplayName(user);
  const farmName = useFarmName();

  const handleDrawerToggle = () => {
    if (mobileOpen) {
      // Closing drawer - remove focus from current element first
      if (
        document.activeElement &&
        document.activeElement instanceof HTMLElement
      ) {
        document.activeElement.blur();
      }
      setMobileOpen(false);
    } else {
      // Opening drawer
      setMobileOpen(true);
    }
  };

  const handleDrawerClose = () => {
    // Remove focus from any active element before closing
    if (
      document.activeElement &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }

    setMobileOpen(false);

    // Use requestAnimationFrame to ensure DOM updates complete before focusing
    requestAnimationFrame(() => {
      const menuButton = document.querySelector(
        '[aria-label="open drawer"]'
      ) as HTMLElement;
      if (menuButton) {
        menuButton.focus();
      }
    });
  };

  /**
   * Navigate from a drawer menu item.
   *
   * The drawer closes as an urgent update so its exit animation runs. The
   * navigation is a transition: while a lazily loaded route chunk downloads,
   * React keeps the current page mounted instead of swapping the whole layout
   * for the Suspense fallback in AppRoutes. Without this the close committed
   * while the tree was hidden, the backdrop's exit transition never ran, and a
   * dark overlay stayed over the new page until reload (AG-267).
   */
  const handleMenuNavigate = (path: string) => {
    if (isMobile) {
      handleDrawerClose();
    }
    startTransition(() => {
      navigate(path);
    });
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    }
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    signOutRedirect();
    handleProfileMenuClose();
  };

  // Build URL for external Agronod Konto pages with redirect and account params
  const buildAgronodKontoUrl = (path: string) => {
    const baseUrl = window.appConfig.REACT_APP_AGRONOD_KONTO_URL;
    const redirectUrl = encodeURIComponent(window.location.href);
    return `${baseUrl}${path}?redirectUrl=${redirectUrl}&agronodkontoId=${selectedKontoId ?? ''}`;
  };

  // Main navigation items. `badge` renders a small status pill after the
  // label — used while Gårdspärmen's content is still under development.
  const mainMenuItems: {
    text: string;
    icon: React.ReactNode;
    path: string;
    badge?: string;
  }[] = [
    {
      text: "Hem",
      icon: <NavHemIcon sx={{ fontSize: 20 }} />,
      path: ROUTES.DASHBOARD,
    },
    {
      text: "Kontroller",
      icon: <NavKontrollerIcon sx={{ fontSize: 20 }} />,
      path: ROUTES.CONTROLS,
    },
    {
      text: "Gårdspärm",
      icon: <NavDokumentIcon sx={{ fontSize: 20 }} />,
      path: ROUTES.DOCUMENTS,
      badge: "Tidig version",
    },
  ];

  // Utility navigation items (hidden for now)
  const utilityMenuItems: { text: string; icon: React.ReactNode; path: string }[] = [
    // {
    //   text: "Notiser",
    //   icon: <NavNotiserIcon sx={{ fontSize: 20 }} />,
    //   path: ROUTES.DASHBOARD,
    // },
    // {
    //   text: "Inställningar",
    //   icon: <NavInstallningarIcon sx={{ fontSize: 20 }} />,
    //   path: ROUTES.SETTINGS,
    // },
  ];

  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        px: 1,
        py: 2,
      }}
    >
      {/* Logo */}
      <Box sx={{ pt: 1, pb: 6, px: 2 }}>
        <BrandLogo width={115} />
      </Box>

      {/* Konto selector card */}
      {availableKonton.length > 0 && (() => {
        const hasMultiple = availableKonton.length > 1;
        const displayName = farmName;

        // Konto-switch needs to be surgically ordered so the page you
        // were on can't fire any more requests:
        // 1. Cancel in-flight fetches — otherwise a pending request
        //    resolves with the new konto header and hits 404/500
        //    under the new scope.
        // 2. Drop all cached queries so no subscriber can re-render
        //    stale data under the new konto.
        // 3. Navigate to dashboard so the current detail page
        //    unmounts in the same batched commit as the konto change
        //    — its hooks cannot schedule new fetches.
        // 4. Finally update konto state (and sessionStorage).
        //    useApiClient's invalidate effect still fires for
        //    same-route switches (user already on /dashboard), which
        //    is the only path that actually needs it.
        const handleKontoSelect = (nextKontoId: string) => {
          setKontoAnchorEl(null);
          if (nextKontoId === selectedKontoId) return;
          queryClient.cancelQueries();
          queryClient.removeQueries();
          navigate(ROUTES.DASHBOARD);
          setSelectedKontoId(nextKontoId);
        };

        const openKontoMenu = (target: HTMLElement) => setKontoAnchorEl(target);

        return (
          <Box sx={{ mb: 3 }}>
            <Box
              {...(hasMultiple && {
                role: "button",
                tabIndex: 0,
                "aria-label": `Byt gård. Aktuell gård: ${displayName ?? ""}`,
                "aria-haspopup": "menu" as const,
                "aria-expanded": Boolean(kontoAnchorEl),
                onClick: (e: React.MouseEvent<HTMLElement>) =>
                  openKontoMenu(e.currentTarget),
                onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openKontoMenu(e.currentTarget);
                  }
                },
              })}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                bgcolor: "#FFF8E5",
                border: "1px solid",
                borderColor: "rgba(230, 179, 79, 0.5)",
                borderRadius: "16px",
                boxShadow: "0px 1px 2px 0px rgba(37, 35, 33, 0.04)",
                cursor: hasMultiple ? "pointer" : "default",
                outline: "none",
                "&:hover": hasMultiple
                  ? { borderColor: "primary.medium" }
                  : undefined,
                "&:focus-visible": hasMultiple
                  ? {
                      borderColor: "primary.medium",
                      boxShadow:
                        "0px 0px 0px 2px rgba(230, 179, 79, 0.4), 0px 1px 2px 0px rgba(37, 35, 33, 0.04)",
                    }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: "primary.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FarmIcon sx={{ fontSize: 22, color: "text.primary" }} />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  mx: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                <Typography
                  sx={{
                    color: "rgba(71, 70, 67, 0.7)",
                    fontSize: "11px",
                    fontWeight: 600,
                    lineHeight: "13.2px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    fontFamily: "Inter, sans-serif",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Aktiv gård
                </Typography>
                <Typography
                  sx={{
                    color: "text.primary",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: "16.9px",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    fontFamily: "Inter, sans-serif",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </Typography>
              </Box>

              {hasMultiple && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: "5px",
                    borderRadius: "15px",
                    flexShrink: 0,
                    transition: "background-color 150ms ease",
                    "&:hover": {
                      bgcolor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <SwapHoriz sx={{ fontSize: 20, color: "text.primary" }} />
                </Box>
              )}
            </Box>

            {hasMultiple && (
              <Menu
                anchorEl={kontoAnchorEl}
                open={Boolean(kontoAnchorEl)}
                onClose={() => setKontoAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                marginThreshold={0}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: kontoAnchorEl?.offsetWidth ?? 240,
                      mt: 0.5,
                    },
                  },
                }}
              >
                {availableKonton.map((k) => (
                  <MenuItem
                    key={k.KontoId}
                    selected={k.KontoId === selectedKontoId}
                    onClick={() => handleKontoSelect(k.KontoId)}
                  >
                    {k.KontoNamn}
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Box>
        );
      })()}

      {/* Main navigation */}
      <List sx={{ pt: 1 }}>
        {mainMenuItems.map((item) => {
          const isSelected =
            item.path === ROUTES.CONTROLS
              ? location.pathname.startsWith("/controls")
              : location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => handleMenuNavigate(item.path)}
                sx={{
                  borderRadius: "64px",
                  px: 1,
                  py: 1,
                  minHeight: "56px",
                  gap: 1,
                  bgcolor: isSelected ? "#E5B34F" : "transparent",
                  "&:hover": {
                    bgcolor: isSelected ? "#E5B34F" : "rgba(0, 0, 0, 0.04)",
                  },
                  "&.Mui-selected": {
                    bgcolor: "#E5B34F",
                    "&:hover": {
                      bgcolor: "#E5B34F",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      color: "hsla(45, 3%, 27%, 1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    color: "text.primary",
                    "& .MuiListItemText-primary": {
                      fontSize: "16px",
                      fontWeight: 500,
                      fontFamily: "Inter, sans-serif",
                    },
                  }}
                />
                {item.badge && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1,
                      py: 0.25,
                      borderRadius: "999px",
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      lineHeight: 1.4,
                      // Flat pill; readable on both the idle bg and the
                      // yellow selected pill.
                      bgcolor: isSelected
                        ? "rgba(255, 255, 255, 0.55)"
                        : "grey.200",
                      color: "text.secondary",
                      flexShrink: 0,
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Utility navigation */}
      <Box sx={{ borderTop: "1px solid rgba(37, 35, 33, 0.1)", pt: 1, pb: 1 }}>
        <List>
          {utilityMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleMenuNavigate(item.path)}
                sx={{
                  px: 1,
                  py: 1,
                  minHeight: "56px",
                  gap: 1,
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      color: "hsla(45, 3%, 27%, 1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    color: "text.primary",
                    "& .MuiListItemText-primary": {
                      fontSize: "16px",
                      fontWeight: 500,
                      fontFamily: "Inter, sans-serif",
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* User profile. A real button (not a click-only Box) so the menu is
          reachable by keyboard, and so the browser knows the menu was opened
          with a pointer and leaves the first item without a focus ring. */}
      <Box
        role="button"
        tabIndex={0}
        aria-label="Användarmeny"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        aria-controls="primary-search-account-menu"
        onClick={handleProfileMenuOpen}
        onKeyDown={handleProfileMenuKeyDown}
        sx={{
          px: 0,
          py: 0,
          cursor: "pointer",
          borderRadius: "8px",
          outline: "none",
          // Same ring as the konto switcher above, so keyboard users see where they are.
          "&:focus-visible": {
            boxShadow: "0px 0px 0px 2px rgba(230, 179, 79, 0.4)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1,
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.04)",
              borderRadius: "8px",
            },
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#51697E",
              color: "white",
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            {userDisplayName?.charAt(0).toUpperCase() || "?"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                fontWeight: 500,
                fontSize: "16px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: "22px",
              }}
            >
              {userDisplayName || "Användare"}
            </Typography>
            <Typography
              sx={{
                color: "rgba(37, 35, 33, 0.69)",
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                lineHeight: "16px",
              }}
            >
              {isAdmin ? "ADMIN" : "ANVÄNDARE"}
            </Typography>
          </Box>
          <ExpandMore sx={{ color: "text.primary", fontSize: 24 }} />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* App Bar - Show when drawer is hidden (below 1400px) */}
      <AppBar
        position="fixed"
        sx={{
          display: isMobile ? "flex" : "none",
          borderRadius: 0,
        }}
      >
        <Toolbar sx={{ position: "relative" }}>
          {/* Left side buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            <IconButton color="inherit" onClick={() => navigate(ROUTES.HOME)}>
              <NavHemIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Centered logo */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <BrandLogo height={24} />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Right side buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            {/* Notifications icon hidden for now
            <IconButton
              color="inherit"
              onClick={() => navigate(ROUTES.DASHBOARD)}
            >
              <NavNotiserIcon sx={{ fontSize: 20 }} />
            </IconButton>
            */}

            <IconButton
              size="large"
              edge="end"
              aria-label="Användarmeny"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "#51697E",
                  color: "white",
                }}
              >
                {userDisplayName?.charAt(0).toUpperCase() || "?"}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User menu - for both mobile and desktop */}
      <Menu
        id="primary-search-account-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
            },
          },
        }}
      >
        {/* Selected konto header */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mr: 1,
            }}
          >
            {farmName || "Mitt konto"}
          </Typography>
        </Box>

        {/* Menu items - external links to Agronod Konto */}
        <MenuItem
          onClick={() => {
            window.location.href = buildAgronodKontoUrl('/hem');
            handleProfileMenuClose();
          }}
          sx={{
            height: 48,
            gap: 1,
          }}
        >
          <NavHemIcon sx={{ fontSize: 20, color: "icon.primary" }} />
          <Typography variant="body1">Mitt Agronod</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            window.location.href = buildAgronodKontoUrl('/tjanster');
            handleProfileMenuClose();
          }}
          sx={{
            height: 48,
            gap: 1,
          }}
        >
          <NavKontrollpanelIcon sx={{ fontSize: 20, color: "icon.primary" }} />
          <Typography variant="body1">Tjänster</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            window.location.href = buildAgronodKontoUrl('/mitt-konto');
            handleProfileMenuClose();
          }}
          sx={{
            height: 48,
            gap: 1,
          }}
        >
          <NavForetagAnvandareIcon
            sx={{ fontSize: 20, color: "icon.primary" }}
          />
          <Typography variant="body1">Företag & användare</Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogout}
          sx={{
            height: 48,
            gap: 1,
          }}
        >
          <Typography variant="body1">Logga ut</Typography>
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0
        }}
        aria-label="navigation menu"
      >
        {/* Temporary drawer - shown below 1400px */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
            closeAfterTransition: false, // Fix aria-hidden accessibility warning
            disableAutoFocus: true, // Prevent automatic focus on drawer open
            disableRestoreFocus: true, // Prevent focus restoration conflicts
          }}
          sx={{
            display: isMobile ? "block" : "none",
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "primary.main",
              color: "text.primary",
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
              border: "none",
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Permanent drawer - shown at 1400px and above */}
        <Drawer
          variant="permanent"
          sx={{
            display: isMobile ? "none" : "block",
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "primary.main",
              color: "text.primary",
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
              border: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
          minHeight: "100vh",
          backgroundColor: "background.page",
          overflowX: "hidden",
        }}
      >
        <Toolbar sx={{ display: isMobile ? "block" : "none" }} />
        {children}
      </Box>

      {/* Support Button - Global floating support button - Always at bottom, moves up when toolbar is visible */}
      <Box
        sx={(theme) => ({
          position: "fixed",
          bottom: toolbarVisible
            ? FLOATING_ELEMENTS.TOOLBAR.BOTTOM +
              toolbarHeight +
              FLOATING_ELEMENTS.TOOLBAR.GAP
            : FLOATING_ELEMENTS.SUPPORT.BOTTOM,
          right: FLOATING_ELEMENTS.SUPPORT.RIGHT,
          zIndex: FLOATING_ELEMENTS.SUPPORT.Z_INDEX,
          display: isMobile ? "none" : "block", // Show only on larger desktops (>= 1400px)
          transition: "bottom 0.3s ease-in-out", // Smooth transition when toolbar appears/disappears

          [theme.breakpoints.down("lg")]: {
            right: "8px",
          },
        })}
      >
        <SupportButton
          buttonSize="large"
          QALink="https://www.agronod.com/fragor"
          supportEmail="support@agronod.com"
          supportTelephone="010-18 61 060"
        />
      </Box>
    </Box>
  );
};

export default MainLayout;
